import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApplyView from './components/ApplyView';
import Studio from './components/Studio';
import { applyForCreator, fetchCreatorCenter, fetchHomeDisplayConfig, type CreatorCenterData } from '@/api/member';
import { ensureMemberAccessToken } from '@/utils/memberSession';

export default function CinemaCreatorCenterPage() {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [data, setData] = useState<CreatorCenterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void fetchHomeDisplayConfig()
      .then((config) => {
        if (config.creatorCenterEnabled === false) {
          navigate('/', { replace: true });
          return Promise.resolve<CreatorCenterData | null>(null);
        }
        return ensureMemberAccessToken().then((token) =>
          token ? fetchCreatorCenter(token) : Promise.reject(new Error('unauthorized'))
        );
      })
      .then((result) => {
        if (result) setData(result);
      })
      .catch(() => navigate('/login'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const apply = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) throw new Error('unauthorized');
      setData(await applyForCreator(token));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('cinema_creatorApplyFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-14 md:pt-16 pb-20 md:pb-24 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 md:px-10 lg:px-14">
        <div className="py-4 md:py-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-background-900/60 border border-background-800/60 text-foreground-400 hover:text-foreground-200 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div>
            <h1 className="text-lg md:text-2xl font-bold font-heading text-foreground-50">
              {t('cinema_creatorCenter')}
            </h1>
            <p className="text-xs leading-relaxed text-foreground-500 mt-0.5">{t('cinema_creatorCenterDesc')}</p>
            <p className="mt-1 flex items-center gap-1 text-xs font-medium leading-relaxed text-amber-400">
              <i className="ri-error-warning-line shrink-0" />
              <span>{t('cinema_creatorCenterNote')}</span>
            </p>
          </div>
        </div>

        {loading && !data ? (
          <div className="min-h-72 flex items-center justify-center text-foreground-500"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
        ) : !data?.isCreator ? (
          <ApplyView onApply={() => void apply()} loading={loading} error={error} />
        ) : (
          <Studio initialData={data} onDataChange={setData} />
        )}
      </div>
    </div>
  );
}
